/*
 * Copyright (c) 2026 Paxia AI Pty Ltd. All rights reserved.
 * This software is proprietary and confidential.
 * No part of this codebase may be copied, modified, published, distributed, sublicensed, or used without prior written permission from Paxia AI Pty Ltd.
 */

export const ACCESS_FEATURE = "feature";
export const ACCESS_PUBLIC = "public";
export const ACCESS_INTERNAL = "internal";
export const ACCESS_ADMIN_ONLY = "admin_only";
export const ACCESS_BILLING_SAFE = "billing_safe";

const featureSurface = (surfaceKey, options = {}) => ({
  surfaceKey,
  defaultAccess: ACCESS_FEATURE,
  fallbackTo: "/dashboard",
  ...options,
});

const safeSurface = (surfaceKey, defaultAccess = ACCESS_BILLING_SAFE, options = {}) => ({
  surfaceKey,
  defaultAccess,
  fallbackTo: "/dashboard",
  ...options,
});

const adminSurface = (surfaceKey, options = {}) => ({
  surfaceKey,
  defaultAccess: ACCESS_ADMIN_ONLY,
  requiresStaff: true,
  fallbackTo: "/dashboard",
  ...options,
});

const actionSurface = (surfaceKey, options = {}) => ({
  surfaceKey,
  defaultAccess: ACCESS_FEATURE,
  ...options,
});

export const APP_ROUTE_SURFACES = {
  dashboard: featureSurface("frontend_route:app:dashboard"),
  accounts: featureSurface("frontend_route:app:accounts"),
  accountsLegacy: featureSurface("frontend_route:app:accounts_legacy"),
  accountTradeHistory: featureSurface("frontend_route:app:account_trade_history"),
  tradeHistoryDetail: featureSurface("frontend_route:app:trade_history_detail"),
  selectAccountForHistory: featureSurface("frontend_route:app:select_account_for_history"),
  settings: featureSurface("frontend_route:app:settings"),
  chartSnapshotConfigs: featureSurface("frontend_route:app:chart_snapshot_configs"),
  themeSettings: featureSurface("frontend_route:app:theme_settings"),
  notificationSettings: featureSurface("frontend_route:app:notification_settings"),
  analysis: featureSurface("frontend_route:app:analysis"),
  analysisJobDetail: featureSurface("frontend_route:app:analysis_job_detail"),
  winRate: featureSurface("frontend_route:app:win_rate"),
  selectCTraderAccount: featureSurface("frontend_route:app:select_ctrader_account"),
  botsList: featureSurface("frontend_route:app:bots_list"),
  botCreate: featureSurface("frontend_route:app:bot_create"),
  botDetail: featureSurface("frontend_route:app:bot_detail"),
  botVersions: featureSurface("frontend_route:app:bot_versions"),
  botVersionCreate: featureSurface("frontend_route:app:bot_version_create"),
  botVersionDetail: featureSurface("frontend_route:app:bot_version_detail"),
  backtestConfigCreate: featureSurface("frontend_route:app:backtest_config_create"),
  backtestConfigEdit: featureSurface("frontend_route:app:backtest_config_edit"),
  strategyBuilder: featureSurface("frontend_route:app:strategy_builder"),
  strategyBuilderV2: featureSurface("frontend_route:app:strategy_builder_v2"),
  liveRuns: featureSurface("frontend_route:app:live_runs"),
  backtestRunDetail: featureSurface("frontend_route:app:backtest_run_detail"),
  studio: featureSurface("frontend_route:app:studio"),
  liveTrade: featureSurface("frontend_route:app:live_trade"),
  monitoring: adminSurface("frontend_route:app:monitoring"),
  portfolio: featureSurface("frontend_route:app:portfolio"),
  approvals: featureSurface("frontend_route:app:approvals"),
  approvalDetail: featureSurface("frontend_route:app:approval_detail"),
  alerts: featureSurface("frontend_route:app:alerts"),
  alertDetail: featureSurface("frontend_route:app:alert_detail"),
  researchHub: featureSurface("frontend_route:app:research_hub"),
  economicCalendar: featureSurface("frontend_route:app:economic_calendar"),
  news: featureSurface("frontend_route:app:news"),
  marketMind: featureSurface("frontend_route:app:market_mind"),
  cotReport: featureSurface("frontend_route:app:cot_report"),
  economicIndicators: featureSurface("frontend_route:app:economic_indicators"),
  options: featureSurface("frontend_route:app:options"),
  knowledgeGraph: featureSurface("frontend_route:app:knowledge_graph"),
  insiderTrading: featureSurface("frontend_route:app:insider_trading"),
  market: featureSurface("frontend_route:app:market"),
  screener: featureSurface("frontend_route:app:screener"),
  explorer: featureSurface("frontend_route:app:explorer"),
  ai: featureSurface("frontend_route:app:ai"),
  profile: safeSurface("frontend_route:app:profile"),
  subscription: safeSurface("frontend_route:app:subscription"),
  billing: safeSurface("frontend_route:app:billing"),
  onboarding: safeSurface("frontend_route:app:onboarding"),
  platformAdmin: adminSurface("frontend_route:app:platform_admin"),
};

export const APP_ACTION_SURFACES = {
  mainAssistantSend: actionSurface("frontend_action:app:main_assistant_send", {
    label: "send assistant messages",
  }),
  strategyBuilderAiSend: actionSurface("frontend_action:app:strategy_builder_ai_send", {
    label: "use AI strategy chat",
  }),
  strategyBuilderBacktest: actionSurface("frontend_action:app:strategy_builder_backtest", {
    label: "run backtests",
  }),
  botVersionLaunchBacktest: actionSurface("frontend_action:app:bot_version_launch_backtest", {
    label: "launch backtests",
  }),
  botVersionRerunBacktest: actionSurface("frontend_action:app:bot_version_rerun_backtest", {
    label: "re-run backtests",
  }),
  botVersionStartLiveRun: actionSurface("frontend_action:app:bot_version_start_live_run", {
    label: "start live runs",
  }),
};

export const AI_ROUTE_SURFACES = {
  hub: featureSurface("frontend_route:ai:hub", { fallbackTo: "/dashboard" }),
  providers: adminSurface("frontend_route:ai:providers", { fallbackTo: "/ai" }),
  models: adminSurface("frontend_route:ai:models", { fallbackTo: "/ai" }),
  embeddingModels: adminSurface("frontend_route:ai:embedding_models", { fallbackTo: "/ai" }),
  systems: featureSurface("frontend_route:ai:systems", { fallbackTo: "/ai" }),
  systemDetail: featureSurface("frontend_route:ai:system_detail", { fallbackTo: "/ai" }),
  mlOverview: adminSurface("frontend_route:ai:ml_overview", { fallbackTo: "/ai" }),
  mlDatasets: adminSurface("frontend_route:ai:ml_datasets", { fallbackTo: "/ai" }),
  mlFeatures: adminSurface("frontend_route:ai:ml_features", { fallbackTo: "/ai" }),
  mlModels: adminSurface("frontend_route:ai:ml_models", { fallbackTo: "/ai" }),
  mlModelManager: adminSurface("frontend_route:ai:ml_model_manager", { fallbackTo: "/ai" }),
  mlModel3d: adminSurface("frontend_route:ai:ml_model_3d", { fallbackTo: "/ai" }),
  mlTrainingJobs: adminSurface("frontend_route:ai:ml_training_jobs", { fallbackTo: "/ai" }),
  mlExperiments: adminSurface("frontend_route:ai:ml_experiments", { fallbackTo: "/ai" }),
  mlDeployments: adminSurface("frontend_route:ai:ml_deployments", { fallbackTo: "/ai" }),
  experts: featureSurface("frontend_route:ai:experts", { fallbackTo: "/ai" }),
  expertDetail: featureSurface("frontend_route:ai:expert_detail", { fallbackTo: "/ai" }),
  snippets: featureSurface("frontend_route:ai:snippets", { fallbackTo: "/ai" }),
  snippetDetail: featureSurface("frontend_route:ai:snippet_detail", { fallbackTo: "/ai" }),
  indicatorSets: featureSurface("frontend_route:ai:indicator_sets", { fallbackTo: "/ai" }),
  indicatorSetDetail: featureSurface("frontend_route:ai:indicator_set_detail", { fallbackTo: "/ai" }),
  agents: featureSurface("frontend_route:ai:agents", { fallbackTo: "/ai" }),
  agentDetail: featureSurface("frontend_route:ai:agent_detail", { fallbackTo: "/ai" }),
  agentChat: featureSurface("frontend_route:ai:agent_chat", { fallbackTo: "/ai" }),
  schedules: featureSurface("frontend_route:ai:schedules", { fallbackTo: "/ai" }),
  executionDetail: featureSurface("frontend_route:ai:execution_detail", { fallbackTo: "/ai" }),
  analytics: featureSurface("frontend_route:ai:analytics", { fallbackTo: "/ai" }),
  globalStorage: featureSurface("frontend_route:ai:global_storage", { fallbackTo: "/ai" }),
  serviceInstructions: adminSurface("frontend_route:ai:service_instructions", { fallbackTo: "/ai" }),
};

export const AI_ACTION_SURFACES = {
  floatingAssistantSend: actionSurface("frontend_action:ai:floating_assistant_send", {
    label: "send assistant messages",
  }),
  agentChatSend: actionSurface("frontend_action:ai:agent_chat_send", {
    label: "send agent messages",
  }),
  systemBuilderSend: actionSurface("frontend_action:ai:system_builder_send", {
    label: "send AI Manager messages",
  }),
  scheduleTrigger: actionSurface("frontend_action:ai:schedule_trigger", {
    label: "run schedules now",
  }),
  systemBacktestStart: actionSurface("frontend_action:ai:system_backtest_start", {
    label: "start AI system backtests",
  }),
  systemTestRun: actionSurface("frontend_action:ai:system_test_run", {
    label: "run AI system tests",
  }),
};

export function evaluateSurfaceAccess(surface, accessSnapshot) {
  if (!surface) {
    return { allowed: true, configured: true, reason: null, featureCode: null };
  }

  const dbSurface = accessSnapshot?.surfaces?.[surface.surfaceKey];
  if (dbSurface) {
    return {
      allowed: dbSurface.allowed !== false,
      configured: dbSurface.configured !== false,
      reason: dbSurface.allowed === false ? dbSurface.reason || "feature_off" : dbSurface.reason || null,
      featureCode: dbSurface.featureCode || null,
      statusCode: dbSurface.statusCode,
    };
  }

  if (surface.requiresStaff || surface.defaultAccess === ACCESS_ADMIN_ONLY) {
    const isStaff = accessSnapshot?.user?.is_staff === true;
    return {
      allowed: isStaff,
      configured: true,
      reason: isStaff ? null : "staff_required",
      featureCode: surface.featureCode || null,
    };
  }

  if ([ACCESS_PUBLIC, ACCESS_INTERNAL, ACCESS_BILLING_SAFE].includes(surface.defaultAccess)) {
    return { allowed: true, configured: true, reason: null, featureCode: surface.featureCode || null };
  }

  if (surface.defaultAccess !== ACCESS_FEATURE) {
    return { allowed: true, configured: true, reason: null, featureCode: null };
  }

  return {
    allowed: true,
    configured: false,
    reason: "surface_unmapped",
    featureCode: null,
  };
}

export function emitFeatureGate(detail) {
  if (typeof window === "undefined" || !detail?.featureCode) {
    return;
  }
  window.dispatchEvent(
    new CustomEvent("paxia:feature-gate", {
      detail: {
        feature: detail.featureCode,
        reason: detail.reason || "feature_off",
      },
    }),
  );
}
